import { Card } from '@components/common/Card/Card';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useGetDictionariesQuery } from '@/api/dictionaries/api';
import { EventProtocolTypeDictionaryItem } from '@/api/dictionaries/types';
import { useGetOrganizationsShortQuery } from '@/api/organizations/api';
import {
  useGetRegistryRecordQuery,
  useUpdateRegistryRecordMutation,
} from '@/api/vaccination/registry/api';
import { IndividualShort, Protocol } from '@/api/vaccination/registry/types';
import { FARMING } from '@/constants/routes';
import { cardSchema } from '@/pages/Vaccination/components/registry/card/card.config';
import { columns } from '@/pages/Vaccination/components/registry/card/columns.config';
import { editSchema } from '@/pages/Vaccination/components/registry/card/edit.config';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { vaccination } from '@/shared/security/permissions/vaccination';
import { IndividualStatus } from '@/shared/vaccination/enums';

import cl from './style.module.scss';

type ProtocolTypeSection = {
  key: string;
  animalSpecies: string;
  types: ProtocolTypeItem[];
};

type ProtocolTypeItem = {
  key: string;
  eventProtocolType: string;
  protocols: Protocol[];
};

export const VaccinationRegistryCard = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id = '' } = useParams();

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const { data: dictionaries } = useGetDictionariesQuery();
  const { data: record, isLoading } = useGetRegistryRecordQuery({ id }, { skip: !id });
  const [updateRecord, { isLoading: isUpdateLoading }] =
    useUpdateRegistryRecordMutation();

  const { data: organizations } = useGetOrganizationsShortQuery({
    level: OrganizationLevelType.ORGANIZATION,
  });

  const { data: structurals } = useGetOrganizationsShortQuery(
    {
      level: OrganizationLevelType.STRUCTURAL_DIVISION,
      parentId: record?.organizationId,
    },
    { skip: !record?.organizationId },
  );

  const locationLink = useMemo(() => {
    return (
      FARMING +
      `/${record?.individualLocationId}?orgId=${record?.structuralDivisionId}&id=${record?.facilityId}&facilityName=${record?.facilityName}&parentId=${record?.organizationId}&activeCell=${record?.id}`
    );
  }, [record]);

  const sections = useMemo<ProtocolTypeSection[]>(
    () =>
      Object.values(
        Object.entries(record?.protocolsByType || {}).reduce<Record<string, any>>(
          (sections, [key, protocols]) => {
            const eventProtocolType = dictionaries?.dictMap.EVENT_PROTOCOL_TYPE[
              key
            ] as EventProtocolTypeDictionaryItem;

            if (!eventProtocolType) return sections;

            const animalSpecies = dictionaries?.dictMap.ANIMAL_SPECIES[
              eventProtocolType.animalSpeciesId || ''
            ] ?? { id: 'unassigned', name: 'Без привязки к группе' };

            const section = (sections[animalSpecies.id] ??= {
              key: animalSpecies.id,
              animalSpecies: animalSpecies.name,
              types: {},
            });

            section.types[eventProtocolType.id] = {
              key: eventProtocolType.id,
              eventProtocolType: eventProtocolType.name,
              protocols: protocols,
            };

            return sections;
          },
          {},
        ),
      ).map((section) => ({
        ...section,
        types: Object.values(section.types),
      })),
    [dictionaries, record],
  );

  return (
    <>
      <PageTitle title={'Паспорт особи'} />
      <Card
        viewMode
        schema={cardSchema(dictionaries, organizations, structurals, locationLink)}
        data={record}
        loading={isLoading || isUpdateLoading}
        actions={{
          onBack: () =>
            state
              ? navigate({ pathname: state.pathname, search: state.search })
              : navigate(-1),
          onEdit: {
            action: () => setIsEditModalOpen(true),
            display: record?.individualStatus !== IndividualStatus.DEAD,
            security: {
              permissions: vaccination.individual.editPassport,
              level: [
                OrganizationLevelType.ORGANIZATION,
                OrganizationLevelType.STRUCTURAL_DIVISION,
              ],
            },
          },
        }}
      />
      <PageTitle
        level={4}
        title={'Мониторинг проведения лечебно-профилактических мероприятий'}
        subTitle={true}
      />
      <div className={cl.wrap}>
        {sections.map((section) => (
          <section key={section.key} className={cl.section}>
            <PageTitle
              level={4}
              title={section.animalSpecies}
              className={cl.sectionTitle}
            />
            {section.types.map((item) => (
              <div key={item.key}>
                <PageTitle level={5} title={item.eventProtocolType} />
                <UTable
                  size={'small'}
                  data={item?.protocols || []}
                  columns={columns(dictionaries?.dictMap)}
                  loading={isLoading || isUpdateLoading}
                />
              </div>
            ))}
          </section>
        ))}
      </div>

      <Modal
        open={isEditModalOpen}
        mode={'form'}
        formKey={'editModal'}
        title={'Редактировать особь'}
        data={record}
        schema={editSchema(dictionaries, organizations, structurals)}
        onSubmit={async (values: IndividualShort) => {
          if (record) {
            await updateRecord({
              ...values,
              diseaseDescription:
                values?.individualStatus === IndividualStatus.SICK
                  ? values.diseaseDescription
                  : null,
              deadRemovalDate: [IndividualStatus.DEAD, IndividualStatus.REMOVAL].includes(
                values?.individualStatus,
              )
                ? values.deadRemovalDate
                : null,
              id: record.id,
            }).unwrap();
            setIsEditModalOpen(false);
          }
        }}
        onCancel={() => setIsEditModalOpen(false)}
        width={'375px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />
    </>
  );
};
